import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

const SCROLL_EDGE = 100
const SCROLL_MAX = 10
const TOUCH_DRAG_THRESHOLD = 8

export default class extends Controller {
  static values = { query: String }

  connect() {
    this.element.addEventListener("mousedown", this.markDragSource)
    this.element.addEventListener("dragstart", this.dragStart)
    this.element.addEventListener("dragover", this.dragOver)
    this.element.addEventListener("drop", this.drop)
    this.element.addEventListener("dragend", this.clear)
    this.element.addEventListener("touchstart", this.touchStart, { passive: true })
    this.element.addEventListener("touchmove", this.touchMove, { passive: false })
    this.element.addEventListener("touchend", this.touchEnd)
    this.element.addEventListener("touchcancel", this.touchCancel)
    document.addEventListener("turbo:before-stream-render", this.beforeStreamRender)
  }

  disconnect() {
    this.element.removeEventListener("mousedown", this.markDragSource)
    this.element.removeEventListener("dragstart", this.dragStart)
    this.element.removeEventListener("dragover", this.dragOver)
    this.element.removeEventListener("drop", this.drop)
    this.element.removeEventListener("dragend", this.clear)
    this.element.removeEventListener("touchstart", this.touchStart)
    this.element.removeEventListener("touchmove", this.touchMove)
    this.element.removeEventListener("touchend", this.touchEnd)
    this.element.removeEventListener("touchcancel", this.touchCancel)
    document.removeEventListener("turbo:before-stream-render", this.beforeStreamRender)
    this.clear()
  }

  beforeStreamRender = (event) => {
    const target = event.target.getAttribute("target")
    if (!target?.startsWith("task_list_")) return

    if (!this.pendingColumnScrolls) {
      this.pendingColumnScrolls = this.captureColumnScrolls()
      this.pendingBoardScroll = this.element.scrollLeft
    }

    const status = target.replace(/^task_list_/, "")
    const scrollTop = this.pendingColumnScrolls[status]
    const render = event.detail.render

    event.detail.render = (streamElement) => {
      render(streamElement)

      const body = this.element.querySelector(`#${CSS.escape(target)}`)
      if (body && scrollTop != null) body.scrollTop = scrollTop
      if (this.pendingBoardScroll != null) this.element.scrollLeft = this.pendingBoardScroll
    }

    queueMicrotask(() => {
      this.pendingColumnScrolls = null
      this.pendingBoardScroll = null
    })
  }

  captureColumnScrolls() {
    return Object.fromEntries(
      [...this.element.querySelectorAll(".task-column-body[data-status]")].map((body) => [
        body.dataset.status,
        body.scrollTop
      ])
    )
  }

  markDragSource = (event) => {
    if (event.button != null && event.button !== 0) {
      this.dragSourceCard = null
      return
    }

    this.dragSourceCard = this.cardFromBodySource(event.target)
  }

  dragStart = (event) => {
    const card = this.dragSourceCard
    if (!card) {
      event.preventDefault()
      return
    }

    const rect = card.getBoundingClientRect()
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", card.dataset.taskId)
    event.dataTransfer.setDragImage(card, event.clientX - rect.left, event.clientY - rect.top)
    this.beginDrag(card, event.clientX, event.clientY)
    document.addEventListener("dragover", this.trackPointer)
  }

  trackPointer = (event) => {
    if (!this.dragged) return

    this.lastX = event.clientX
    this.lastY = event.clientY
  }

  dragOver = (event) => {
    if (!this.dragged) return

    const column = event.target.closest(".task-column-body")
    if (!column) return

    event.preventDefault()
    this.updateInsertion(column, event.clientY)
  }

  drop = async (event) => {
    if (!this.dragged || this.committing) return

    const column = event.target.closest(".task-column-body")
      || this.columnAt(event.clientX, event.clientY)
    if (!column) return

    event.preventDefault()
    this.updateInsertionFromPoint(event.clientX, event.clientY)
    await this.commitMove(column)
  }

  touchStart = (event) => {
    if (event.touches.length !== 1) return

    const card = this.cardFromBodySource(event.target)
    if (!card) return

    const touch = event.touches[0]
    this.touchId = touch.identifier
    this.touchOriginX = touch.clientX
    this.touchOriginY = touch.clientY
    this.pendingCard = card
  }

  touchMove = (event) => {
    const touch = this.findTouch(event.touches) || this.findTouch(event.changedTouches)
    if (!touch) return

    if (!this.dragged && this.pendingCard) {
      const distance = Math.hypot(touch.clientX - this.touchOriginX, touch.clientY - this.touchOriginY)
      if (distance < TOUCH_DRAG_THRESHOLD) return

      this.beginDrag(this.pendingCard, touch.clientX, touch.clientY)
      this.createGhost(touch.clientX, touch.clientY)
      this.pendingCard = null
      this.touchDragging = true
    }

    if (!this.touchDragging || !this.dragged) return

    event.preventDefault()
    this.lastX = touch.clientX
    this.lastY = touch.clientY
    this.moveGhost(touch.clientX, touch.clientY)
    this.updateInsertionFromPoint(touch.clientX, touch.clientY)
  }

  touchEnd = async (event) => {
    if (this.touchId != null && !this.findTouch(event.changedTouches)) return

    if (!this.touchDragging || !this.dragged) {
      this.resetTouch()
      return
    }

    event.preventDefault()
    const column = this.columnAt(this.lastX, this.lastY)
    if (column) {
      this.updateInsertionFromPoint(this.lastX, this.lastY)
      await this.commitMove(column)
    } else {
      this.clear()
    }
  }

  touchCancel = () => {
    if (this.touchDragging || this.pendingCard) this.clear()
  }

  beginDrag(card, clientX, clientY) {
    this.dragged = card
    this.dragged.classList.add("task-dragging")
    this.lastX = clientX
    this.lastY = clientY
    this.startAutoScroll()
  }

  async commitMove(column) {
    if (!this.dragged || this.committing) return

    this.committing = true
    const id = this.dragged.dataset.taskId
    const status = column.dataset.status
    const beforeId = this.before?.dataset.taskId || ""
    const afterId = this.after?.dataset.taskId || ""
    const query = this.queryValue || ""

    const body = new URLSearchParams({
      "task[status]": status,
      "task[before_id]": beforeId,
      "task[after_id]": afterId
    })
    const headers = {
      Accept: "text/vnd.turbo-stream.html",
      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
    }
    const csrfToken = document.querySelector("meta[name=csrf-token]")?.content
    if (csrfToken) headers["X-CSRF-Token"] = csrfToken

    try {
      const response = await fetch(`/tasks/${id}?query=${encodeURIComponent(query)}`, {
        method: "PATCH",
        headers,
        body
      })

      if (!response.ok) throw new Error()
      Turbo.renderStreamMessage(await response.text())
    } catch {
      Turbo.visit(window.location.href, { action: "replace" })
    } finally {
      this.committing = false
      this.clear()
    }
  }

  clear = () => {
    this.stopAutoScroll()
    document.removeEventListener("dragover", this.trackPointer)
    this.removeGhost()
    this.dragged?.classList.remove("task-dragging")
    this.element.querySelectorAll(".task-drop-before, .task-drop-end").forEach((element) => {
      element.classList.remove("task-drop-before", "task-drop-end")
    })
    this.dragged = null
    this.before = null
    this.after = null
    this.lastX = null
    this.lastY = null
    this.dragSourceCard = null
    this.resetTouch()
  }

  resetTouch() {
    this.pendingCard = null
    this.touchId = null
    this.touchOriginX = null
    this.touchOriginY = null
    this.touchDragging = false
  }

  isInteractive(target) {
    return !!target.closest("button, input, select, textarea, label, form")
  }

  cardFromBodySource(target) {
    if (this.isInteractive(target)) return null

    const body = target.closest(".task-card-body")
    if (!body) return null

    return body.closest(".task-card")
  }

  cards(column) {
    return [...column.querySelectorAll(".task-card")].filter((card) => card !== this.dragged)
  }

  updateInsertion(column, clientY) {
    const cards = this.cards(column)
    this.before = cards.find((card) => clientY < card.getBoundingClientRect().top + card.offsetHeight / 2)
    this.after = this.before ? cards[cards.indexOf(this.before) - 1] : cards.at(-1)
    this.showInsertion(column)
  }

  updateInsertionFromPoint(clientX, clientY) {
    const column = this.columnAt(clientX, clientY)
    if (!column) return

    const rect = column.getBoundingClientRect()
    const y = Math.min(Math.max(clientY, rect.top), rect.bottom)
    this.updateInsertion(column, y)
  }

  columnAt(clientX, clientY) {
    if (clientX == null) return null

    if (clientY != null) {
      const under = document.elementFromPoint(clientX, clientY)?.closest(".task-column-body")
      if (under) return under
    }

    return this.columnAtX(clientX)
  }

  columnAtX(clientX) {
    return [...this.element.querySelectorAll(".task-column-body")].find((column) => {
      const rect = column.getBoundingClientRect()
      return clientX >= rect.left && clientX <= rect.right
    }) || null
  }

  showInsertion(column) {
    this.element.querySelectorAll(".task-drop-before, .task-drop-end").forEach((element) => {
      element.classList.remove("task-drop-before", "task-drop-end")
    })
    this.before ? this.before.classList.add("task-drop-before") : column.classList.add("task-drop-end")
  }

  findTouch(touchList) {
    if (this.touchId == null || !touchList) return null
    return [...touchList].find((touch) => touch.identifier === this.touchId) || null
  }

  createGhost(clientX, clientY) {
    this.removeGhost()

    const rect = this.dragged.getBoundingClientRect()
    this.ghostOffsetX = clientX - rect.left
    this.ghostOffsetY = clientY - rect.top
    this.ghost = this.dragged.cloneNode(true)
    this.ghost.classList.add("task-drag-ghost")
    this.ghost.classList.remove("task-dragging")
    this.ghost.removeAttribute("id")
    this.ghost.removeAttribute("draggable")
    this.ghost.style.width = `${rect.width}px`
    document.body.appendChild(this.ghost)
    this.moveGhost(clientX, clientY)
  }

  moveGhost(clientX, clientY) {
    if (!this.ghost) return

    this.ghost.style.transform = `translate3d(${clientX - this.ghostOffsetX}px, ${clientY - this.ghostOffsetY}px, 0)`
  }

  removeGhost() {
    this.ghost?.remove()
    this.ghost = null
    this.ghostOffsetX = null
    this.ghostOffsetY = null
  }

  startAutoScroll() {
    this.stopAutoScroll()

    const tick = () => {
      if (!this.dragged) return

      this.autoScroll()
      this.scrollFrame = requestAnimationFrame(tick)
    }

    this.scrollFrame = requestAnimationFrame(tick)
  }

  stopAutoScroll() {
    if (this.scrollFrame != null) {
      cancelAnimationFrame(this.scrollFrame)
      this.scrollFrame = null
    }
  }

  autoScroll() {
    if (this.lastX == null || this.lastY == null) return

    const boardRect = this.element.getBoundingClientRect()
    const dx = this.edgeScrollDelta(this.lastX, boardRect.left, boardRect.right)
    if (dx) this.element.scrollBy(dx, 0)

    const column = this.columnAt(this.lastX, this.lastY)
    if (column) {
      const rect = column.getBoundingClientRect()
      const dy = this.edgeScrollDelta(this.lastY, rect.top, rect.bottom)
      if (dy) column.scrollBy(0, dy)
    }

    this.updateInsertionFromPoint(this.lastX, this.lastY)
  }

  edgeScrollDelta(position, start, end) {
    if (position < start + SCROLL_EDGE) return -SCROLL_MAX
    if (position > end - SCROLL_EDGE) return SCROLL_MAX
    return 0
  }
}
